import { Component, signal, OnInit, Inject, PLATFORM_ID, Optional } from '@angular/core';
import { RouterOutlet, Router, NavigationEnd } from '@angular/router';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Store } from '@ngrx/store';
import { filter } from 'rxjs/operators';
import { Analytics } from '@angular/fire/analytics';
import { environment } from '../environments/environment';
import { LoadingComponent } from './shared/components/loading/loading.component';
import { HeaderComponent } from './shared/components/header/header.component';
import { FooterComponent } from './shared/components/footer/footer.component';
import { selectIsAppLoading } from './core/store/auth/auth.selectors';
import { SeoService } from './core/services/seo.service';
import { AnalyticsService } from './core/services/analytics.service';
import { MaintenanceService } from './core/services/maintenance.service';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, CommonModule, LoadingComponent, HeaderComponent, FooterComponent],
  templateUrl: './app.html',
  styleUrl: './app.scss'
})
export class App implements OnInit {
  protected readonly title = signal(environment.appName);
  protected readonly environment = environment;
  
  isAppLoading$;
  isBackoffice = false;
  isMaintenancePage = false;
  isMaintenanceMode = false;

  constructor(
    private store: Store, 
    private seo: SeoService,
    private router: Router,
    private analyticsService: AnalyticsService,
    private maintenanceService: MaintenanceService,
    @Inject(PLATFORM_ID) private platformId: Object,
    @Optional() @Inject(Analytics) private analytics: Analytics | null
  ) {
    this.isAppLoading$ = this.store.select(selectIsAppLoading);
    
    // Rileva se siamo nel backoffice o nella pagina di manutenzione
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      this.isBackoffice = event.url.startsWith('/backoffice');
      this.isMaintenancePage = event.url === '/maintenance';
      
      // Disabilita/abilita lo scroll del body in base alla pagina
      if (isPlatformBrowser(this.platformId)) {
        if (this.isMaintenancePage) {
          document.body.style.overflow = 'hidden';
          document.body.style.height = '100vh';
        } else {
          document.body.style.overflow = '';
          document.body.style.height = '';
        }
      }
    });
  }

  ngOnInit(): void {        
    this.seo.initRouteMetaUpdates();
    
    // Inizializza Analytics solo nel browser
    if (isPlatformBrowser(this.platformId)) {
      if (this.analytics) {
        this.analyticsService.init(this.analytics);
      } else {
        console.warn('⚠️ Firebase Analytics non disponibile - potrebbe essere un problema di configurazione');
        // Prova a inizializzare Analytics direttamente se non è stato iniettato
        this.initAnalyticsManually();
      }

      // Precaria lo stato di manutenzione all'avvio per migliorare le performance
      this.maintenanceService.getMaintenanceStatus().subscribe({
        next: (status) => {
          this.isMaintenanceMode = status.isMaintenanceMode;
        },
        error: (error) => {
          console.warn('Errore nel caricamento dello stato di manutenzione:', error);
        }
      });

      // Ascolta i cambiamenti dello stato di manutenzione
      this.maintenanceService.getMaintenanceStatus$().subscribe({
        next: (status) => {
          if (status) {
            this.isMaintenanceMode = status.isMaintenanceMode;
          }
        }
      });
    }
  }

  private initAnalyticsManually(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    try {
      // Prova a ottenere Analytics direttamente da Firebase
      import('@angular/fire/analytics').then(({ getAnalytics }) => {
        import('@angular/fire/app').then(({ getApp }) => {
          try {
            const app = getApp();
            const analytics = getAnalytics(app);
            this.analyticsService.init(analytics);
            console.log('✅ Firebase Analytics inizializzato manualmente');
          } catch (error) {
            console.error('❌ Errore nell\'inizializzazione manuale di Analytics:', error);
          }
        });
      });
    } catch (error) {
      console.error('❌ Errore nel caricamento di Analytics:', error);
    }
  }
}
